import { ToastService } from 'src/app/services/toast.service';
import { inject } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { SubscriptionService } from '../services/subscription.service';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { Subscription } from 'rxjs';
import { catchError, of } from 'rxjs';

import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DowngradeCleanupComponent } from '../subscription-downgrade/modals/downgrade-cleanup/downgrade-cleanup.component';
import { MapDeleteConfirmationComponent } from '../subscription-downgrade/modals/map-delete-confirmation/map-delete-confirmation.component';

@Component({
    selector: 'app-subscription',
    templateUrl: './subscription.component.html',
    styleUrls: ['./subscription.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SubscriptionComponent implements OnInit, OnDestroy {
  private readonly toasts = inject(ToastService);
  userSubscription: Subscription;
  userId: number | string;
  userEmail: string;
  isUser: boolean = true;
  isLoadingPlans: boolean = true;

  plans: { month: any[]; year: any[] } = { month: [], year: [] };
  currentSubscription: any = null;
  selectedInterval: 'month' | 'year' = 'month';

  constructor(
    private subscriptionService: SubscriptionService,
    private currentUserService: CurrentUserService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
      this.activatedRoute.queryParams.subscribe((params) => {
      this.isUser = params['isUser'] !== 'false';
    
          this.cdr.detectChanges();});
    this.getStripeProducts()

    // Allow viewing without login - load plans for all users
    this.userSubscription = this.currentUserService.currentUser.subscribe((user) => {
      if (user) {
        this.userId = user.id;
        this.userEmail = user.email;
        // Load plans with user context
        this.loadPlans();
      } else {
        // User not logged in - still load plans but without user context
        this.userId = null;
        this.userEmail = null;
        this.currentSubscription = null;
        this.loadPlans();
      }
    
          this.cdr.detectChanges();});
  }

  getStripeProducts() {
    this.subscriptionService.getStripeProducts().subscribe((res: any) => {
      console.log(res);
    
          this.cdr.detectChanges();});
  }

  loadPlans(): void {
    this.isLoadingPlans = true;
    // Pass userId only if user is logged in (can be null/undefined)
    this.subscriptionService
      .getSubscriptionPlans(this.userId)
      .pipe(
        catchError((error) => {
          console.error('Failed to load subscription plans:', error);
          this.isLoadingPlans = false;
          return of(null);
        })
      )
      .subscribe((res: any) => {
        if (res) {
          const plans = res.plans || { month: [], year: [] };

          // Transform features for all plans


          this.plans = plans;
          // Only set current subscription if user is logged in
          this.currentSubscription = (this.userId && res.current_subscription) ? res.current_subscription : null;

          // Transform features for current subscription if exists
          if (this.currentSubscription && this.currentSubscription.features) {
            this.currentSubscription.features = this.transformFeaturesToObject(this.currentSubscription.features);
          }

          if (this.currentSubscription && this.currentSubscription.interval === 'year') {
            this.selectedInterval = 'year';
          } else {
            this.selectedInterval = 'month';
          }

        }
        this.isLoadingPlans = false;
        this.cdr.detectChanges();
      });
  }

  onIntervalChange(interval: 'month' | 'year'): void {
    this.selectedInterval = interval;
  }

  getCurrentPlanTier(): number {
    if (!this.currentSubscription) return 0;
    
    // Combine lists
    const allPlans = [...(this.plans.month || []), ...(this.plans.year || [])];
    let match: any = null;

    // 1. Match by Stripe Price ID
    if (this.currentSubscription.stripe_price_id) {
      match = allPlans.find(p => p.price_id === this.currentSubscription.stripe_price_id);
    }
    // 2. Match by Plan ID
    else if (this.currentSubscription.plan_id) {
      match = allPlans.find(p => p.id === this.currentSubscription.plan_id);
    }
    // 3. Match by Name
    else {
      const currentName = (this.currentSubscription.plan_name || this.currentSubscription.plan_level || '').toLowerCase();
      match = allPlans.find(p => p.name.toLowerCase() === currentName);
    }

    return match ? (match.stripe_index || 0) : 0;
  }

  // HELPER: Get Current Price
  getCurrentPrice(): number {
    if (!this.currentSubscription) return 0;

    // Combine monthly and yearly plans to find the match
    const allPlans = [...(this.plans.month || []), ...(this.plans.year || [])];
    
    // 1. Try matching by Stripe Price ID (Most Accurate)
    if (this.currentSubscription.stripe_price_id) {
      const match = allPlans.find(p => p.price_id === this.currentSubscription.stripe_price_id);
      if (match) return match.price;
    }

    // 2. Try matching by Plan ID
    if (this.currentSubscription.plan_id) {
      const match = allPlans.find(p => p.id === this.currentSubscription.plan_id);
      if (match) return match.price;
    }

    // 3. Try matching by Name
    const currentName = (this.currentSubscription.plan_name || this.currentSubscription.plan_level || '').toLowerCase();
    if (currentName) {
      const match = allPlans.find(p => p.name.toLowerCase() === currentName);
      if (match) return match.price;
    }

    return 0;
  }

  isCurrentPlan(plan: any): boolean {
    // If user is not logged in, no plan is current
    if (!this.userId || !this.currentSubscription || !plan) {
      return false;
    }
  
    const currentPlanName = (this.currentSubscription.plan_name || this.currentSubscription.plan_level || '').toLowerCase();
    const planName = (plan.name || '').toLowerCase();

    if (currentPlanName === 'adventurer' && planName === 'adventurer') {
      return true; // Mark as current regardless of selectedInterval
    }

    if (plan.interval && this.selectedInterval && plan.interval !== this.selectedInterval) {
      return false;
    }
    
    // Primary check: stripe_price_id (most reliable and includes interval)
    if (this.currentSubscription.stripe_price_id && plan.price_id) {
      return this.currentSubscription.stripe_price_id === plan.price_id;
    }
    
    // Check if plan has is_current_plan flag (from Stripe API) - but verify interval matches
    if (plan.is_current_plan !== undefined) {
      // Double-check interval matches
      if (plan.interval && this.selectedInterval && plan.interval !== this.selectedInterval) {
        return false;
      }
      return plan.is_current_plan;
    }
    
    // Fallback: check by plan_id (but still verify interval)
    if (this.currentSubscription.plan_id && plan.id) {
      if (plan.interval && this.selectedInterval && plan.interval !== this.selectedInterval) {
        return false;
      }
      return this.currentSubscription.plan_id === plan.id;
    }
    
    // Special fallback for Adventurer plan: check by plan name/level
    // This handles cases where user has Adventurer plan from registration but no Stripe price_id
    if (this.currentSubscription.plan_name || this.currentSubscription.plan_level) {
      const currentPlanName = (this.currentSubscription.plan_name || this.currentSubscription.plan_level || '').toLowerCase();
      const planName = (plan.name || '').toLowerCase();
      
      // If both are "adventurer" (free plan), consider it current
      if (currentPlanName === 'adventurer' && planName === 'adventurer') {
        // For free plan, don't check interval - it's the same plan regardless
        return true;
      }
    }
    
    return false;
  }

  shouldShowUpgrade(plan: any): boolean {
    // 1. Don't show for the plan we are currently on
    if (this.isCurrentPlan(plan)) return false;
    
    // 2. Safety checks
    if (!this.userId || !this.currentSubscription || !plan) return false;

    // 3. FREE USER CHECK:
    // If user has no Stripe Subscription (Free Plan), they must see "Subscribe".
    // Return FALSE here so "shouldShowSubscribe" returns true.
    const hasStripeSubscription =
      this.currentSubscription.has_stripe_customer &&
      this.currentSubscription.stripe_subscription_id;

    if (!hasStripeSubscription) {
      return false; 
    }

    // 4. SIMPLE PRICE LOGIC:
    const currentPrice = this.getCurrentPrice();
    const newPrice = Number(plan.price || 0);
    console.log('currentPrice', currentPrice);
    console.log('newPrice', newPrice);
    // If new price is LOWER, it is NOT an upgrade.
    if (newPrice < currentPrice) {
      return false;
    }

    // If new price is HIGHER (or EQUAL), it IS an upgrade.
    return true;
  }

  shouldShowSubscribe(plan: any): boolean {
    if (!plan) {
      return false;
    }

    // Priority 1: Don't show if current plan
    if (this.isCurrentPlan(plan)) {
      return false;
    }

    // Priority 2: Don't show if upgrade should be shown
    if (this.shouldShowUpgrade(plan)) {
      return false;
    }

    // Priority 3: Don't show if downgrade should be shown
    if (this.shouldShowDowngrade(plan)) {
      return false;
    }

    // If user is not logged in, show subscribe button (will redirect to login)
    if (!this.userId) {
      return true;
    }

    // If user has no subscription, show subscribe
    if (!this.currentSubscription) {
      return true;
    }

    const hasStripeSubscription =
      this.currentSubscription.has_stripe_customer &&
      this.currentSubscription.stripe_subscription_id;

    // If user has free plan without Stripe, check if this is their current plan
    if (!hasStripeSubscription) {
      // If this is the current plan (Adventurer), don't show subscribe
      if (this.isCurrentPlan(plan)) {
        return false;
      }
      // Otherwise show subscribe for other plans
      return true;
    }

    // If user has paid subscription, show subscribe for same or higher tier plans
    const currentPlan = this.getCurrentPlanFromList();
    if (currentPlan) {
      // Only show subscribe for same or higher tier plans (not lower - those show downgrade)
      return plan.stripe_index >= currentPlan.stripe_index;
    }

    // Fallback: show subscribe if we can't determine current plan
    return true;
  }

  shouldShowDowngrade(plan: any): boolean {
    // 1. Don't show for the plan we are currently on
    if (!plan || this.isCurrentPlan(plan)) return false;
    
    // 2. Safety checks
    if (!this.userId || !this.currentSubscription) return false;

    // 3. FREE USER CHECK:
    // Free users cannot downgrade.
    const hasStripeSubscription =
      this.currentSubscription.has_stripe_customer &&
      this.currentSubscription.stripe_subscription_id;

    if (!hasStripeSubscription) {
      return false;
    }

    // 4. SIMPLE PRICE LOGIC:
    const currentPrice = this.getCurrentPrice();
    const newPrice = Number(plan.price || 0);

    // If new price is strictly LESS than current price, it is a Downgrade.
    if (newPrice < currentPrice) {
      return true;
    }

    return false;
  }

  getCurrentPlanFromList(): any {
    if (!this.currentSubscription) {
      return null;
    }

    // Get current plan from the SAME interval (monthly or yearly)
    const currentIntervalPlans = this.plans[this.selectedInterval] || [];
    
    // First try to match by stripe_price_id (most accurate)
    if (this.currentSubscription.stripe_price_id) {
      const matched = currentIntervalPlans.find((p) => p.price_id === this.currentSubscription.stripe_price_id);
      if (matched) {
        return matched;
      }
    }
    
    // Fallback: match by plan_id
    if (this.currentSubscription.plan_id) {
      return currentIntervalPlans.find((p) => p.id === this.currentSubscription.plan_id);
    }
    
    return null;
  }

  onSubscribeClick(plan: any): void {
    if (!plan) {
      return;
    }

    // If user is not logged in, redirect to login
    if (!this.userId) {
      this.router.navigate(['/login'], {
        queryParams: { returnTo: '/subscription' }
      });
      return;
    }

    // If user already has a paid Stripe subscription (any interval),
    // manage all changes via the Stripe customer portal instead of Checkout.
    const hasStripeSubscription =
      this.currentSubscription &&
      this.currentSubscription.has_stripe_customer &&
      this.currentSubscription.stripe_subscription_id;

    if (hasStripeSubscription) {
      this.manageBilling();
      return;
    }

    // For new subscriptions (no existing paid subscription), create checkout session
    const priceId = plan.price_id;

    this.subscriptionService
      .createCheckoutSession(this.userId, null, priceId)
      .pipe(
        catchError((error) => {
          console.error('Failed to create checkout session:', error);
          this.toasts.showNote("We couldn't start checkout. Please try again.", 'danger');
          return of(null);
        })
      )
      .subscribe((res: { checkout_url: string } | null) => {
        if (res?.checkout_url) {
          window.location.href = res.checkout_url;
        }
      
          this.cdr.detectChanges();});
  }

  onDowngradeClick(plan: any): void {
    // Downgrade always goes through customer portal
    if (!this.userId) {
      this.router.navigate(['/login'], {
        queryParams: { returnTo: '/subscription' }
      });
      return;
    }

    this.manageBilling();

    // this.startDowngradeFlow(plan, this.currentSubscription);

  }

  onUpgradeClick(plan: any): void {
    // If user is not logged in, redirect to login
    if (!this.userId) {
      this.router.navigate(['/login'], {
        queryParams: { returnTo: '/subscription' }
      });
      return;
    }

    // For upgrade/downgrade, redirect to customer portal where user can manage subscription
    this.manageBilling();
  }

  // Removed checkAndRedirectToPortal - all users now see the pricing page

  manageBilling(): void {
    if (!this.userId) {
      this.router.navigate(['/login']);
      return;
    }

    this.subscriptionService
      .redirectToCustomPortal(this.userId)
      .pipe(
        catchError((error) => {
          console.error('Error redirecting to customer portal:', error);
          return of(null);
        })
      )
      .subscribe((res: { portal_url: string }) => {
        if (res?.portal_url) {
          window.location.href = res.portal_url;
        }
      
          this.cdr.detectChanges();});
  }

  getFeatureList(features: any): string[] {
    // If features is already an array of strings (from Stripe), return it directly
    if (Array.isArray(features)) {
      return features;
    }

    // Fallback: if features is an object, convert it (for backward compatibility)
    if (!features || typeof features !== 'object') {
      return [];
    }

    const featureList: string[] = [];
    const featureLabels: { [key: string]: string } = {
      max_active_characters: 'Active Characters',
      maps_uploaded: 'Maps',
      max_games_created: 'Games Created',
      max_games_joined: 'Games Joined',
      max_retired_npcs: 'Retired NPCs',
      max_party_members: 'Party Members',
      max_content: 'Content Items',
      max_content_city: 'City Content',
      journaling_allowed: 'Journaling',
      game_creation_allowed: 'Create Games',
      join_games: 'Join Games',
    };

    for (const [key, value] of Object.entries(features)) {
      if (value && featureLabels[key]) {
        if (typeof value === 'boolean' && value) {
          featureList.push(featureLabels[key]);
        } else if (typeof value === 'number') {
          if (value === -1) {
            featureList.push(`Unlimited ${featureLabels[key]}`);
          } else {
            featureList.push(`${value} ${featureLabels[key]}`);
          }
        }
      }
    }

    return featureList;
  }


  transformFeaturesToObject(features: any): any {
    if (!features || !Array.isArray(features)) {
      return features;
    }

    const featureLabels: { [key: string]: string } = {
      max_active_characters: 'Active Characters',
      maps_uploaded: 'Maps',
      max_games_created: 'Games Created',
      max_games_joined: 'Games Joined',
      max_retired_npcs: 'Retired NPCs',
      max_party_members: 'Party Members',
      max_content: 'Content Items',
      max_content_city: 'City Content',
      journaling_allowed: 'Journaling',
      game_creation_allowed: 'Create Games',
      join_games: 'Join Games',
    };

    // Invert the mapping
    const labelToKey: { [key: string]: string } = {};
    for (const [key, label] of Object.entries(featureLabels)) {
      labelToKey[label] = key;
    }

    const featuresObj: any = {};
    features.forEach((featureStr: string) => {
      if (typeof featureStr !== 'string') return;

      let value: any = true;
      let label = featureStr.trim();

      if (label.startsWith('Unlimited ')) {
        value = -1;
        label = label.replace('Unlimited ', '').trim();
      } else {
        const match = label.match(/^(\d+)\s+(.+)$/);
        if (match) {
          value = Number(match[1]);
          label = match[2].trim();
        }
      }

      const key = labelToKey[label];
      if (key) {
        featuresObj[key] = value;
      }
    });

    return featuresObj;
  }
}
